import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
// Lottie
import Lottie from "lottie-react";
// __apis__
import { fetchUserDetails } from "src/__apis__/accounts";
import {
  checkClientFormRefillStatusRequest,
  checkClientFormStatusRequest,
} from "src/__apis__/clientInfo";
// atoms
import userAtom from "src/recoil/atoms/userAtom";
// animations
import pleaseWaitAnimation from "src/assets/animations/please-wait.json";
//
import DashboardLayout from "./dashboard";
import LogoOnlyLayout from "./LogoOnlyLayout";
import LoadingScreen from "src/components/LoadingScreen";
import { Box, Typography } from "@mui/material";
import clientFormRefillStatusAtom from "src/recoil/atoms/clientFormRefillStatusAtom";
import { subscriptionProfileFetcher } from "src/__apis__/subscriptionProfile";
import subscriptionProfileAtom from "src/recoil/atoms/subscriptionProfileAtom";

// -------------------------------------------------------------------------------------------------------------

const MainLoader = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box sx={{ width: { xs: 300, md: 400 } }}>
        <Lottie animationData={pleaseWaitAnimation} />
      </Box>
      <Typography variant="h6" color="grey.700">
        Don't get frustrated if i took long time loading, the experience is
        really worth it ❤️.
      </Typography>
      <Typography sx={{ mt: 1 }} variant="h2" color="primary">
        Your Profile Is Loading, Please Wait.
      </Typography>
    </Box>
  );
};

// -------------------------------------------------------------------------------------------------------------

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function LayoutGenerator({ children }) {
  const [authenticated, setAuthenticated] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [redirectToSpecialLayout, setRedirectToSpecialLayout] = useState(false);
  const [refillFormStatus, setRefillFormStatus] = useRecoilState(
    clientFormRefillStatusAtom,
  );
  const [userCountry, setUserCountry] = useState(null);

  const [isCountryLoading, setIsCountryLoading] = useState(true);
  const [countryFetchError, setCountryFetchError] = useState(null);

  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);

  const query = useQuery();
  const reviewProgram = query.get("review-program");

  const setSubscriptionProfile = useSetRecoilState(subscriptionProfileAtom);

  useEffect(() => {
    // Fetch user's physical country location
    const fetchCountry = async () => {
      try {
        console.log("Fetching country location...");
        // Add cache busting and use db-ip to avoid VPN DNS hijack issues with 1.1.1.1
        const response = await fetch("https://api.db-ip.com/v2/free/self?t=" + new Date().getTime(), {
          cache: "no-store"
        });
        
        const data = await response.json();
        console.log("Country API response:", data);
        if (data.countryCode) {
          setUserCountry(data.countryCode);
        }
      } catch (error) {
        console.error("Error fetching country location", error);
        setCountryFetchError(error.message || "Fetch failed");
      } finally {
        setIsCountryLoading(false);
      }
    };
    fetchCountry();
  }, []);

  console.log("user country", userCountry);

  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    const reviewProgramStatus = localStorage.getItem("reviewProgram");

    if (accessToken) {
      setAuthenticated(true);
      fetchUserDetails(reviewProgram)
        .then((response) => {
          setUser(response);
          if (!reviewProgram || !reviewProgramStatus) {
            checkClientFormStatus();
            checkClientFormRefillStatus();
          }
        })

        .catch((error) => {
          console.error("Error fetching user details", error);
          setIsCheckingStatus(false);
        });
    } else {
      setAuthenticated(false);
      setIsCheckingStatus(false);
      navigate("/auth/signin");
    }
  }, [navigate, setUser, reviewProgram]);

  const checkClientFormStatus = useCallback(async () => {
    const reviewProgramStatus = localStorage.getItem("reviewProgram");
    if (!reviewProgramStatus) {
      await checkClientFormStatusRequest()
        .then((response) => {
          const { redirect_to_form, redirect_to_waiting_page } = response;

          if (redirect_to_waiting_page) {
            navigate("/form/form-submitted", { replace: true });
            setRedirectToSpecialLayout(true);
          } else if (redirect_to_form) {
            navigate("/form/custom-program", { replace: true });
            setRedirectToSpecialLayout(true);
          } /* else {
            // If neither flag is true, navigate to the home or dashboard page
            navigate("/welcome/home", { replace: true }); // Adjust this to your home page route
          } */
        })
        .catch((error) => {
          console.error("Error checking client form status", error);
        })
        .finally(() => {
          setIsCheckingStatus(false);
        });
    }
  }, [navigate]);

  const checkClientFormRefillStatus = useCallback(async () => {
    const reviewProgramStatus = localStorage.getItem("reviewProgram");
    if (!reviewProgramStatus) {
      await checkClientFormRefillStatusRequest()
        .then((response) => {
          if (response.refill_form) {
            setRefillFormStatus(true);
          }
          console.log("form refill status", response);
        })
        .catch((error) => {
          console.log("Error checking client form refill status", error);
        });
    }
  }, []);

  const fetchSubscriptionProfile = useCallback(async () => {
    await subscriptionProfileFetcher()
      .then((response) => {
        setSubscriptionProfile(response);
      })
      .catch((error) => {
        console.log(
          "Something wrong happened fetching subscription profile",
          error,
        );
      });
  }, []);

  const reviewProgramStatus = localStorage.getItem("reviewProgram");

  const isOutOfRegion = Boolean(
    user?.region?.toLowerCase().trim() === "local" &&
    userCountry &&
    userCountry !== "EG",
  );

  useEffect(() => {
    console.log("Region Check Debug =>", {
      userRegion: user?.region,
      userCountry,
      isOutOfRegion,
      countryFetchError
    });
  }, [user, userCountry, isOutOfRegion, countryFetchError]);

  useEffect(() => {
    if (user?.status === "in-freeze") {
      navigate("/welcome/freeze-active", { replace: true });
    } else if (isOutOfRegion) {
      navigate("/welcome/out-of-region", { replace: true });
    }
  }, [user, isOutOfRegion, navigate]);

  const isLocalUser = user?.region?.toLowerCase() === "local";

  const debugOverlay = query.get("debug-region") === "true" && (
    <Box
      sx={{
        position: "fixed",
        bottom: 10,
        left: 10,
        bgcolor: "rgba(0,0,0,0.85)",
        color: "#0f0",
        p: 2,
        borderRadius: 1,
        zIndex: 99999,
        fontSize: "14px",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        maxWidth: "90%",
        boxShadow: 3,
        pointerEvents: "none"
      }}
    >
      <strong>Region Debug Info:</strong>{"\n"}
      {JSON.stringify({
        userRegion: user?.region || "undefined",
        userCountry: userCountry || "null",
        isOutOfRegion,
        isCountryLoading,
        countryFetchError: countryFetchError || "none"
      }, null, 2)}
    </Box>
  );

  if (
    (!reviewProgramStatus && isCheckingStatus) ||
    (isLocalUser && isCountryLoading)
  ) {
    return (
      <>
        <MainLoader />
        {debugOverlay}
      </>
    );
  }

  if (
    redirectToSpecialLayout ||
    !authenticated ||
    user?.status === "in-freeze" ||
    isOutOfRegion
  ) {
    return (
      <>
        <LogoOnlyLayout>{children}</LogoOnlyLayout>
        {debugOverlay}
      </>
    );
  }

  return (
    <>
      <DashboardLayout>{children}</DashboardLayout>
      {debugOverlay}
    </>
  );
}

export default LayoutGenerator;
